import { useDispatch, useSelector } from "react-redux";

import { useFetchAdminAreaHierarchy } from "@/api/adminArea/userAdminAreaAPI";
import { fetchDatasetStream } from "@/api/dataset/fetchDatasetStream";
import { useMutationDatasetMetadata } from "@/api/dataset/useDatasetAPI";
import { useMutateGetWidgetDefaultConfig } from "@/api/widget/useWidgetAPI";
import {
  generateAttributeFilterOptions,
  handleAdminAreaChangeHelper
} from "@/helpers/datasetAttributeHelper";
import { useGlobalAdminAreaSync } from "@/hooks/globalAdminArea/useGlobalFilterAdminArea";
import { useDataset } from "@/hooks/map/useDataset";
import { enrichAdminAreaFilters } from "@/hooks/project/utils";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import type { RootState } from "@/store";
import { toggleUseGlobalFilter } from "@/store/slice/datasetSlice";
import type { DatasetDetailResponse } from "@/types/dataset";
import type { DatasetPoiParameter } from "@/types/poiParameter";
import type { AdminAreaSettings } from "@/types/spatial";
import { getAvailableDatasets } from "@/utils/dataset";

import { buildPoiDatasetFilter, isAdminAreaSameAsGlobal } from "./utils";

/**
 * Hook that encapsulates the confirm flow for the POI dataset confirmation modal.
 *
 * When the user confirms, it:
 * 1. Fetches dataset metadata for each dataset (sequentially)
 * 2. Streams the dataset data with the POI-specific filters
 * 3. Adds the dataset to the map via `handleAddDataset`
 * 4. Optionally syncs the selected admin area to the global filter if `shouldApplyGlobal` is true
 * 5. Determines whether the new datasets should be linked to the global filter based on the selected admin area and `shouldApplyGlobal` flag
 * 6. When linking to the global filter, it dispatches `toggleUseGlobalFilter` for each new dataset to link it to the global filter
 */

interface HandleConfirmDatasetsParams {
  poiDatasets: DatasetPoiParameter[];
  selectedAdminArea: AdminAreaSettings | null;
  selectedAdminAreaIds?: string[];
  shouldApplyGlobal?: boolean;
  isOverrideGlobalFilter?: boolean;
}

export const usePoiConfirmation = () => {
  const dispatch = useDispatch();
  const { mutateAsync: getDatasetMetadata } = useMutationDatasetMetadata();
  const { handleAddDataset, generateDatasetData } = useDataset();
  const { syncAdminAreaToGlobalFilter } = useGlobalAdminAreaSync();
  const fetchHierarchy = useFetchAdminAreaHierarchy();
  const { isAdminTreeEnabled } = useAppFeatures();

  const { datasets } = useSelector((state: RootState) => state.dataset);
  const {
    adminAreaSettings: globalAdminAreaSettings,
    selectedAreaIds: globalSelectedAreaIds
  } = useSelector((state: RootState) => state.globalFilter);

  const { mutateAsync: fetchWidgetDefaultConfig } =
    useMutateGetWidgetDefaultConfig();

  const existingCount = Object.values(getAvailableDatasets(datasets)).length;
  /**
   * Processes a list of POI datasets sequentially:
   * fetches their data via the stream API and adds them to the map.
   *
   * @param poiDatasets - Flat list of `DatasetPoiParameter` to add
   * @param selectedAdminArea - The currently selected admin area (used when the admin tree is disabled)
   * @param selectedAdminAreaIds - The list of selected admin area IDs (used when the admin tree is enabled)
   * @param shouldApplyGlobal - Whether to sync the selected admin area to the global filter after adding datasets
   * @param isOverrideGlobalFilter - Whether to prevent linking the new datasets to the global filter, even if the selected admin area matches the global filter
   */
  const handleConfirmDatasets = async ({
    poiDatasets,
    selectedAdminArea,
    selectedAdminAreaIds = [],
    shouldApplyGlobal = false,
    isOverrideGlobalFilter = false
  }: HandleConfirmDatasetsParams): Promise<void> => {
    if (!poiDatasets.length) return;
    const isAdminAreaMatchingGlobal = isAdminAreaSameAsGlobal({
      isAdminTreeEnabled,
      selectedAdminArea,
      selectedAdminAreaIds,
      globalAdminAreaSettings,
      globalSelectedAreaIds
    });

    // Stable per-confirmation: compute once outside the loop
    const shouldLinkToGlobalFilter =
      (isAdminAreaMatchingGlobal || shouldApplyGlobal) &&
      !isOverrideGlobalFilter;

    /**
     * Processes a list of POI datasets sequentially **in reverse order** so that
     * each `handleAddDataset` call (which prepends to `visualizationOrder`) results
     * in the original input order being preserved in the final visualization list.
     */
    for (const [reversedIndex, dataset] of [...poiDatasets]
      .reverse()
      .entries()) {
      const index = poiDatasets.length - 1 - reversedIndex;
      const metadata = await getDatasetMetadata({ id: dataset.dataset_id });
      const countryId = metadata?.country_id ?? "";
      const hierarchy = countryId ? await fetchHierarchy(countryId) : [];
      // enrichAdminAreaFilters processes filters in reverse so that each enriched
      // entry is prepended, producing a reversed list. Reversing back here restores
      // the original order expected by the downstream filter pipeline.
      const enrichedFilters = enrichAdminAreaFilters(
        buildPoiDatasetFilter(
          dataset.filters,
          metadata?.schema ?? [],
          isAdminTreeEnabled
        ),
        hierarchy
      ).reverse();

      let filters = enrichedFilters;

      // When the admin area tree is disabled, apply the dropdown-based admin
      // area selection by merging it into the dataset filters.
      if (!isAdminTreeEnabled) {
        const filterOptions = generateAttributeFilterOptions(
          metadata as unknown as DatasetDetailResponse
        );
        filters = handleAdminAreaChangeHelper(
          selectedAdminArea,
          filterOptions,
          enrichedFilters
        );
      }

      const res = await fetchDatasetStream({
        datasetId: dataset.dataset_id,
        filters,
        generateDatasetData
      });

      let widgetDefaultConfig;
      try {
        widgetDefaultConfig = await fetchWidgetDefaultConfig(
          dataset.dataset_id
        );
      } catch (error) {
        console.warn("Failed to fetch widget default config", error);
      }

      if (!res?.rawDataset || !res?.datasetMetadata) continue;

      const newDataset = await handleAddDataset({
        // DatasetMetadata is structurally compatible with DatasetDetailResponse
        datasetMetadata: {
          ...res.datasetMetadata,
          display_name: dataset.dataset_name
        },
        datasetData: res.rawDataset,
        savedFilters: filters,
        attributesSchema: metadata?.schema ?? [],
        index: existingCount + index,
        widget: widgetDefaultConfig?.data
      });

      if (newDataset?.data?.id) {
        dispatch(
          toggleUseGlobalFilter({
            datasetId: newDataset.data.id,
            useGlobalFilter: shouldLinkToGlobalFilter
          })
        );
      }
    }

    if (shouldApplyGlobal) {
      await syncAdminAreaToGlobalFilter({
        adminArea: selectedAdminArea,
        adminAreaIds: selectedAdminAreaIds,
        isAdminTreeEnabled
      });
    }
  };

  return { handleConfirmDatasets };
};
